import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api } from '@/lib/api';
import { Button, Checkbox, Combobox, Field, Input, Modal, MultiCombobox, Textarea } from '@/components/ui';
import type { ComboboxOption, MultiComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { MasterFormModal } from '@/components/catalog-admin/MasterFormModal';
import { useAuth } from '@/lib/use-auth';
import { regionOptions as regionPickerOptions } from '@/components/companies/company-master-options';
import { useReferenceOptions } from '@/lib/use-reference-options';
import type {
  City,
  Cycle,
  Intake,
  Product,
  Project,
  ProjectCreateInput,
  ProjectUpdateInput,
} from '@/lib/types';
import {
  CAPACITY_PLAN_KEYS,
  CITIES_MAX,
  DESCRIPTION_MAX,
  NAME_MAX,
  PRODUCTS_MAX,
  PROJECT_LABELS,
  type CapacityPlanKey,
  type CapacityPlanStrings,
  type ProjectProductSelection,
  capacityPlanBody,
  capacityPlanStrings,
  projectProductsPayload,
  seedsLineItemsOf,
  validateCapacityPlanField,
  validateProjectCityIds,
  validateProjectDate,
  validateProjectDescription,
  validateProjectName,
  validateProjectProductIds,
} from './project-field-rules';

export interface ProjectFormModalProps {
  open: boolean;
  /**
   * Absent (or null) = create, a record = edit. Typed `Project | null` rather
   * than plain optional so a `useState<Project | null>(null)` passes straight
   * through.
   */
  project?: Project | null;
  onClose: () => void;
  /** Called after a successful save with the server's record. */
  onSaved: (saved: Project) => void;
}

/* -------------------------------------------------------------------------
 * Form shape — contract facts from `docs/api/projects.md` (emerge-backend-app):
 *
 *  1. PATCH sends only what changed: body built from an explicit key list,
 *     never a spread record, and is `.strict()` (unknown key = 422). Not
 *     re-sending `productId` means it is not re-validated, so a project on a
 *     since-retired product can still be renamed.
 *
 *  2. `cycleId` is required and the intake must live inside it. Changing the
 *     cycle RESETS the intake (see `setCycle`) — otherwise a stale intake is a
 *     422 naming `intakeId`, a field the user never touched.
 *
 *  3. The four capacity figures (owner 2026-10-27: Institution / Partner ×
 *     planned capacity / target) each hold `0` and `null` (not set) as
 *     different values. Held as strings so `''` (→ `null`) and `'0'` (→ `0`)
 *     stay distinct. Never `?? 0`, never `Number(x) || null`. The legacy
 *     `capacity` key is the server-derived total and is NEVER sent from here.
 *
 *  4. `cityIds` writes, `cities` reads, and a write REPLACES the set. Omit to
 *     leave alone, `[]` to clear, `null` is a 422. Every id must be ACTIVE at
 *     write time; a read resolves retired cities happily — so the set is sent
 *     only when the user actually changed it.
 *
 *     `countryIds` is gone, not deprecated: removed 2026-08-26, and `.strict()`
 *     makes sending it a 422. A project associates cities, and a city carries
 *     its own country.
 *
 *  5. `regionId` is checked EXISTS AND ACTIVE, and `/regions` does not filter
 *     by default — 17 of 22 rows are retired, each a guaranteed 422 at save
 *     time. `useReferenceOptions('regions')` already sends `?isActive=true`,
 *     hence reused here rather than fetched by hand.
 *
 *  5. ⚠️ **`regionIds` is a SET and every member is checked EXISTS AND ACTIVE.**
 *     Migration 095, deployed 2026-08-29. This rule read *"`regionId` is checked
 *     EXISTS AND ACTIVE"* and described a single-valued `Combobox`; both the
 *     read key (`project.region`) and the write key are GONE. Reading the first
 *     yields `undefined`, which rendered a blank picker rather than an error,
 *     and sending the second is a 422 naming the key — **the write failed
 *     loudly and the read failed silently.**
 *
 *     `regionIds` is a whole-set REPLACE with the same three spellings as
 *     `cityIds`: `[…]` replaces, `[]` clears, omitted leaves alone, `null` is a
 *     422. A refusal names EVERY offending id at once, so the chips mark
 *     individually rather than the block.
 *
 *     `/regions` does not filter by default, so `useReferenceOptions('regions')`
 *     is reused rather than a fetch written by hand — it already sends
 *     `?isActive=true`. ⚠️ That narrows **nothing today**: the master holds 6
 *     rows and all 6 are active (the 17 retired ones this rule used to warn
 *     about were DELETED, not retired). Keep sending it — the reason it is
 *     opt-in has not changed, only the data has.
 *
 *     ⚠️ **The regions are never derived from the cities or their countries.**
 *     `Country.regionId` is upstream geography on an unrelated id sequence; the
 *     join returns India correctly and Egypt as "South East Asia" — quietly
 *     wrong, which is worse than obviously wrong. A project declares its
 *     regions; its cities have one each; nothing reconciles them.
 *
 * Every scalar is a string so the loaded record and the live form compare
 * key-by-key with `!==`. `cityIds` and `regionIds` are string arrays compared
 * as sets, and `isActive` is the one real boolean.
 * --------------------------------------------------------------------- */

/* -------------------------------------------------------------------------
 * ⚠️ "No tracking" / "Custom Events" — DO NOT create a reference-data row for
 * either from this form.
 *
 * `regionId: null` / `intakeId: null` (what this form already sends for an
 * empty picker) already render as Xero's "No tracking": `xero-quote.payload.ts
 * #resolveLineTracking` derives Location from the project's region and Tour
 * Type from its intake, and a null half simply contributes no tracking. A real
 * `f_regions`/`d_intakes` row literally named "No tracking" would be actively
 * harmful instead of redundant: as a region it refuses every push
 * (`XERO_TRACKING_LOCATION_UNKNOWN`, at least loud); as an intake, an
 * unrecognised Tour Type is instead created in Xero SILENTLY (the `201` says
 * nothing), permanently adding a tracking option called "No tracking" to the
 * accountant's chart of accounts with no way on our side to detect or undo it.
 *
 * "Custom Events" is a genuine Xero Tour Type with no EMERGE equivalent —
 * `d_intakes` rows belong to a cycle and custom events belong to none, so
 * closing this gap is a backend/product modelling decision, not this form's.
 *
 * Xero's dropdowns open with "No tracking", but that is Xero's label for *no
 * option selected*, not an option in the category. The tenant's option lists,
 * measured on the wire 2026-08-26 (`emerge-backend-app` CLAUDE.md):
 *
 *     Location    India | Africa | Africa 2 | Global |
 *                 Middle East & North Africa | South East Asia
 *     Tour Type   Fall 2022 … Summer 2030 | Custom Events
 *
 * Neither list contains a row called "No tracking". So the EMERGE equivalent
 * of picking it is `regionIds: []` / `intakeId: null`, which this form already
 * sends — and both already produce exactly the Xero behaviour asked for:
 * `xero-quote.payload.ts#resolveLineTracking` derives Location from the
 * project's region and Tour Type from its intake, and a null half simply
 * contributes no tracking to that line. What was MISSING was not an option but
 * a NAME for the empty state: the pickers said "Unclassified" and "The whole
 * cycle" and never mentioned Xero at all. That is what changed below.
 *
 * ---- The two candidate models, and why one of them is dangerous ----------
 *
 *   (a) `null`, labelled — what is implemented. No master data, no push, and
 *       the two systems agree because nothing was invented on either side.
 *   (b) a real `f_regions` / `d_intakes` row literally named "No tracking".
 *
 * (b) is not merely redundant, it is actively harmful, and asymmetrically so:
 *
 *   - As a REGION it would be matched against Xero's curated Location list,
 *     find nothing, and refuse every push with `XERO_TRACKING_LOCATION_UNKNOWN`
 *     — loud, and at least recoverable.
 *   - As an INTAKE it is the opposite failure. An unrecognised Tour Type is
 *     **created in Xero, silently**, and the `201` carries no field saying it
 *     happened. A row named "No tracking" would therefore add a permanent
 *     tracking option called "No tracking" to the accountant's chart of
 *     accounts — a value that reads as the absence of a value — with nothing on
 *     our side able to detect it and no way for us to undo it. Only a person
 *     with Xero access could remove it, and only after noticing.
 *
 * ---- Custom Events is a genuine gap and is NOT ours to close -------------
 *
 * `Custom Events` really is one of Xero's Tour Type options and EMERGE has no
 * intake for it. But `d_intakes` rows belong to a cycle and custom events
 * belong to none, so it needs a modelling decision (a cycle-less intake? a
 * document-level override used as the standard answer?) that is the backend's
 * and the owner's, not this form's. Nothing here fabricates it.
 *
 * ---- The escape hatch that already exists --------------------------------
 *
 * Quotes and invoices carry `xeroTrackingLocation` / `xeroTrackingTourType`
 * (migration 063). `null` on those means "derive from the project" and NEVER
 * "no tracking" — so they are an override for one document, not a second way of
 * saying what this form says. They are where "Custom Events" can be expressed
 * today, and where a project with no region can still be given a Location.
 *
 * DO NOT create a reference-data row from this form to close any of the above.
 * --------------------------------------------------------------------- */

interface FormState {
  name: string;
  /**
   * EVERY product this project sells — a SET since migration 156 (deployed
   * 2026-09-09), where it was `productId: string`.
   *
   * ⚠️ **The FIRST member is the primary**, and that is not a convention this
   * form invented: `d_projects.product_id` is still `NOT NULL`, still the
   * `productId` write key, and the read comes back with it first. `buildCreate`
   * and `buildPatch` both go through `projectProductsBody`, which emits the pair
   * from this one list, so the stored set is exactly what the reader picked.
   *
   * ⚠️ **`[]` is NOT a legal state and `validate` refuses it.** It is the one
   * set on this record that cannot be cleared — see `validateProjectProductIds`.
   * The primary/secondary split is deliberately NOT a concept on screen: there
   * is one control captioned "Products", because with the pair travelling
   * together there is no divergence for a label to explain, and naming a
   * "primary" would invite the reader to manage an ordering the form already
   * keeps correct.
   */
  productIds: string[];
  /**
   * Per CHOSEN product: is it pulled into a new document's line items by the
   * company "+" seeding flow? Keyed by the same string id `productIds` holds,
   * so the two are always read together and never need reconciling.
   *
   * ⚠️ **A MISSING ENTRY MEANS ON, and `seedsLineItemsOf` is the ONE place that
   * is decided.** It is reachable for exactly one reason — a product just added
   * to this draft. `ProjectProductRef.seedsLineItems` is REQUIRED on the wire,
   * so every product the server sent seeds a real boolean in `recordForm` and a
   * stored `false` can never fall through to the default. That is what makes the
   * `??` a default for a value that does not exist yet rather than the
   * coalesce-between-two-live-facts this codebase forbids.
   *
   * ⚠️ **Entries for products since REMOVED from `productIds` are kept, not
   * pruned.** Nothing iterates this map — every reader walks `productIds` and
   * looks each id up — so a stale entry is inert, and keeping it means a
   * mis-click that drops a chip and re-adds it does not silently re-tick a box
   * somebody had deliberately cleared.
   */
  seedsLineItems: Record<string, boolean>;
  cycleId: string;
  /**
   * `''` = the whole cycle (`null`), and in Xero's vocabulary "No tracking" —
   * lines on this project push with no Tour Type. See the tracking block above.
   */
  intakeId: string;
  /**
   * When the project actually runs. `''` = not recorded (`null`).
   *
   * Held as plain `YYYY-MM-DD` strings, exactly as the wire and `<input
   * type="date">` use them — never constructed as a `Date` (`new
   * Date('2026-05-31')` is midnight UTC and renders as 30 May west of
   * Greenwich; `toISOString().slice(0,10)` mirrors the same bug).
   *
   * startDate/endDate are INDEPENDENTLY nullable; all four combinations
   * (closed period, open-ended start, open-ended end, neither) are legal.
   */
  startDate: string;
  /** See `startDate`. `''` = not recorded (`null`). */
  endDate: string;
  /**
   * SEED's regions of focus — a SET since migration 095 (deployed 2026-08-29).
   *
   * ⚠️ **This was `regionId: string`, `''` meaning unclassified.** Both the
   * singular read (`project.region`) and the singular write key are GONE:
   * reading the first yields `undefined` and rendered a blank control, and
   * sending `regionId` is now a 422 naming the key on a `.strict()` schema.
   *
   * `[]` = unclassified, which is a real state rather than a gap, and is also
   * Xero's "No tracking" — lines on this project push with no Location. Held as
   * string ids so they compare with the option values, exactly like `cityIds`.
   */
  regionIds: string[];
  /**
   * The four planned-capacity / target figures, keyed on their write keys.
   * `''` = not set (`null`); `'0'` = zero. Two different things.
   */
  capacityPlan: CapacityPlanStrings;
  description: string;
  /** City ids as strings, so they compare with the option values. */
  cityIds: string[];
  isActive: boolean;
  /** `d_projects.is_virtual_event` — decides the Project Capacity band for a cityless project. */
  isVirtualEvent: boolean;
}

/**
 * ⚠️ Imported from `./project-field-rules`, not declared here — the inline
 * editors on `/projects` write the same nine columns through the same
 * endpoint, so a second copy of a limit or message would silently drift.
 */
const LABELS = PROJECT_LABELS;

/**
 * Paths the API may return in a 422 `details` array that this form can MARK.
 *
 * ⚠️ **These are the WRITE keys, not the labels.** `LABELS` is keyed on them
 * for exactly that reason — the control captioned "Cities" is `cityIds` and the
 * one captioned "Regions of focus" is `regionIds`, which is what a 422's `path`
 * names. Derived from `LABELS` so the list cannot drift from the controls.
 *
 * ⚠️ **`regionId` left this set on 2026-08-29 (migration 095) and `regionIds`
 * took its place.** Had the old key stayed, a 422 naming `regionIds` would have
 * been partitioned as unroutable: shown as an anonymous form-level line instead
 * of on the control that caused it, while still counting towards "one field
 * needs attention, highlighted below" with nothing highlighted.
 *
 * Anything outside it goes to the form-level banner: a path can be non-empty
 * and still bind to nothing, which is the worse case because it looks bindable
 * — the message is routed to an input that does not exist and is never seen.
 */
// `capacity` is excluded: this form no longer has a control for it (it never
// sends the key), so a refusal naming it must reach the banner, not vanish.
const KNOWN_FIELDS: readonly string[] = Object.keys(LABELS).filter((key) => key !== 'capacity');

/*
 * ⚠️ **`products` comes from `PROJECT_LABELS` now — it is no longer appended
 * here.** *SUPERSEDED 2026-09-14: "`products` is appended HERE rather than added
 * to `PROJECT_LABELS`, and that is deliberate … those editors write the
 * membership and never the ticks — so a 422 naming `products` cannot arise
 * there."* **The inline Products cell on `/projects` sends `products` too since
 * 2026-09-14**, so that premise is gone: the key can be refused on either
 * surface and belongs in the shared map, where both routing lists derive it.
 *
 * What has not changed is why it must be in THIS list at all:
 * `routeFieldErrors` partitions on the form's own write keys, so without it a
 * refusal naming `products` shows as an anonymous form-level line while still
 * counting towards "one field needs attention, highlighted below" with nothing
 * highlighted. `productsError` below is what routes it onto the one control a
 * reader can act on.
 */

/** Entity endpoints cap `perPage` at 100. */
const ENTITY_PER_PAGE = 100;
/**
 * `/cities` caps `perPage` at **200** and defaults to 50 — not the
 * countries/currencies 250, and not the entities' 100. The ceiling is asked
 * for rather than the default accepted, so the picker is the whole active list
 * (2 rows today) instead of silently its first page.
 */
const CITIES_PER_PAGE = 200;

function blankForm(): FormState {
  return {
    name: '',
    productIds: [],
    seedsLineItems: {},
    cycleId: '',
    intakeId: '',
    startDate: '',
    endDate: '',
    regionIds: [],
    capacityPlan: capacityPlanStrings(null),
    description: '',
    cityIds: [],
    isActive: true,
    isVirtualEvent: false,
  };
}

/**
 * The saved record as form values — the baseline every PATCH diff is taken
 * against.
 *
 * `cycle` is a `NOT NULL` column typed nullable here only because every join is
 * a LEFT JOIN: a catalog row deleted out of band degrades one embed to null
 * rather than dropping the project.
 *
 * ⚠️ **`products` needs no such guard and gets none.** It is an array, always
 * present and never null, and a project always sells at least its primary — so
 * `[]` here would mean the read genuinely came back empty, which is a state the
 * server's own migration guard aborts a deploy over. Nothing coalesces it.
 */
function recordForm(project: Project | null): FormState {
  if (!project) return blankForm();
  return {
    name: project.name,
    // `products`, never the deprecated `product` — the set is the authority and
    // it already arrives primary-first, so the order this seeds is the order the
    // server will read back and `productIds[0]` stays the primary untouched.
    productIds: project.products.map((product) => String(product.id)),
    // The baseline every tick diff is taken against, and `products` again —
    // never the deprecated `product`. That one is a `MasterRef` resolved from
    // `d_projects.product_id` and carries NO flag at all, so reading it here
    // would default the PRIMARY product to ON on every load and quietly re-tick
    // a box somebody had deliberately cleared, with a 200 and nothing on screen.
    seedsLineItems: Object.fromEntries(
      project.products.map((product) => [String(product.id), product.seedsLineItems]),
    ),
    cycleId: project.cycle ? String(project.cycle.id) : '',
    intakeId: project.intake ? String(project.intake.id) : '',
    startDate: project.startDate ?? '',
    endDate: project.endDate ?? '',
    // `[]` is "unclassified", a real state — it maps straight through, and
    // never to a default region nobody chose. Always an array, never null.
    regionIds: project.regions.map((region) => String(region.id)),
    // A `0` renders as '0', only a real null becomes ''. `capacityPlan`
    // undefined (older API) seeds Institution planned capacity from `capacity`.
    capacityPlan: capacityPlanStrings(project),
    description: project.description ?? '',
    cityIds: project.cities.map((city) => String(city.id)),
    isActive: project.isActive,
    // `undefined` is an older API, not "No" — but the form needs a value, and
    // the baseline uses the same `false`, so an untouched box sends nothing.
    isVirtualEvent: project.isVirtualEvent ?? false,
  };
}

/**
 * Set equality, deliberately order-insensitive.
 *
 * Used for BOTH set-valued columns — `cityIds` and, since migration 095,
 * `regionIds`. Each reads back in its own master's `sortOrder` / name order,
 * never in the order it was sent, so a positional comparison would report a
 * change on every open and re-send a set nobody touched — which, with one
 * retired member in it, is a 422 on a field the user never opened.
 */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(b);
  return a.every((value) => seen.has(value));
}

/**
 * `FormState`'s two product fields as the shared product rules take them.
 *
 * ⚠️ **The product rules themselves live in `./project-field-rules`** —
 * `seedsLineItemsOf`, the missing-entry default behind it, and
 * `projectProductsPayload`, which decides which of the three product write keys
 * a body carries. They were declared HERE until 2026-09-14, and the reason they
 * are not any more is a measured one rather than tidiness: the inline Products
 * cell on `/projects` shipped the destructive `productIds` branch on 2026-09-13
 * while this form had the same rule right, because each surface carried its own
 * copy and only one of them was corrected. One copy is what stops the next
 * divergence, and the argument for every clause is written out there, at the
 * functions. **Do not copy it back here.**
 *
 * This adapter is the only product-payload knowledge left in this file, and it
 * is a shape change and nothing more: `FormState` keeps `productIds` and
 * `seedsLineItems` as two fields because two controls edit them separately,
 * where the shared rules take one selection.
 */
function productSelection(form: FormState): ProjectProductSelection {
  return { ids: form.productIds, seedsLineItems: form.seedsLineItems };
}

/**
 * POST body.
 *
 * `name`, `productId` and `cycleId` always go — `cycleId` is required and does
 * not default, and an absent key is a 422 before the service is even reached.
 * Every optional the user left blank is OMITTED rather than sent as `null` or
 * `''`, so the server's own defaults apply.
 */
function buildCreate(form: FormState): ProjectCreateInput {
  /*
   * ⚠️ `productId` ALWAYS, plus EXACTLY ONE of `productIds` / `products`, from
   * the one selected list. `productId` is required on create — migration 156 did
   * NOT deprecate it — and the set key carries the rest. Sending a set alone
   * would be a 422 naming `productId`; sending the primary alone would create a
   * project selling one product while the reader had picked three, because an
   * ABSENT set on create means "just the primary".
   *
   * Which set key goes is `projectProductsPayload`'s rule, and on a create it
   * reduces to one question: has the reader unticked anything? If not, this body
   * is byte for byte what it was before the tick existed.
   *
   * `validate` has already refused an empty list, which is what makes
   * `productId` reliably present here — the same contract `Number(form.cycleId)`
   * has always relied on one line down.
   */
  const productKeys = projectProductsPayload(productSelection(form), null);
  const body: ProjectCreateInput = {
    name: form.name.trim(),
    // `Number(undefined)` is `NaN`, which the API refuses naming `productId` —
    // an unreachable state written to fail at the server rather than to be
    // papered over with a fabricated id, and the same shape the old singular
    // `Number(form.productId)` had when the box was empty.
    productId: productKeys.productId ?? Number.NaN,
    cycleId: Number(form.cycleId),
  };

  // ⛔ Never both — see `projectProductsPayload`, which cannot return both.
  if (productKeys.productIds) body.productIds = productKeys.productIds;
  if (productKeys.products) body.products = productKeys.products;

  // Omitted rather than `null`: on create the two mean the same thing ("the
  // whole cycle"), so send the smaller claim.
  if (form.intakeId !== '') body.intakeId = Number(form.intakeId);

  // Sent only when entered — omitting creates the project with no period
  // recorded, the normal state. Never derived from the cycle/intake: a
  // project may legitimately run outside its cycle's window. Raw string,
  // never through a `Date`.
  if (form.startDate !== '') body.startDate = form.startDate;
  if (form.endDate !== '') body.endDate = form.endDate;

  // Omitted on create means "created unclassified", which is exactly what an
  // empty picker means — so `[]` would be a longer way of saying the same
  // thing, and `null` is a 422 on this key rather than a third spelling.
  if (form.regionIds.length > 0) body.regionIds = form.regionIds.map(Number);

  // Only non-empty figures go; `'0'` is a real zero and sent. Never `capacity`.
  Object.assign(body, capacityPlanBody(form.capacityPlan, null));

  const description = form.description.trim();
  if (description !== '') body.description = description;

  // Omitted on create means "no cities", same as an empty picker.
  if (form.cityIds.length > 0) body.cityIds = form.cityIds.map(Number);

  // Defaults to `true` server-side, so only the non-default is worth sending.
  if (!form.isActive) body.isActive = false;

  // Defaults to `false` server-side (migration 296), so only `true` is sent.
  if (form.isVirtualEvent) body.isVirtualEvent = true;

  return body;
}

/**
 * PATCH body: the diff, and nothing else.
 *
 * Five rules live here and each one loses data or invents a 422 if dropped:
 *
 *  1. `name`, `productId`, `cycleId` and `isActive` are NOT NULL. They can be
 *     changed but never cleared, so "leave alone" has to be an omission —
 *     sending `null` is a 422 rather than a clear.
 *  2. `intakeId`, the four capacity figures, `startDate`, `endDate` and
 *     `description` are the SCALARS that DO clear. All but `description` send a real `null`;
 *     `description` sends `''`, which the server normalises. The two SETS clear
 *     differently and are rule 3.
 *
 *     ⚠️ **The two dates would also clear on `''`, and are still sent as
 *     `null`.** `''` is a 422 on `intakeId`, `cycleId`, `productId` and
 *     `capacity` on this same endpoint — the id helpers take a number or a
 *     NON-empty string, so `''` satisfies neither. One spelling for "clear"
 *     across the whole body is the only version of this rule a reader can hold;
 *     letting two fields use the looser one teaches a habit that is a 422
 *     everywhere else it is copied to. (⚠️ This list named `regionId` first
 *     until 2026-08-29. That key is gone with migration 095 and is now a 422 as
 *     an unrecognised key, whatever value it carries.)
 *  3. `cityIds` and `regionIds` are each sent only when THAT SET changed, and
 *     each clears with `[]` — never `null`, which is a 422 on both. A retired
 *     city or region still reads back fine but is a 422 on write, so
 *     re-asserting an untouched set can refuse a save for a field the user
 *     never opened.
 *  4. If the cycle moved, the intake rides along in the same body. The server
 *     judges the pair against the record as it will stand AFTER the merge, so
 *     a lone `cycleId` re-validates the STORED intake against the new cycle
 *     and comes back naming `intakeId` — a field not in the request.
 *  5. ⛔ **`productIds` and `products` are ALTERNATIVES, not companions**, and a
 *     body carrying both is a 422 naming both. Every other pair of keys in this
 *     function either travels together (rule 4) or is independent; this is the
 *     one that has to CHOOSE. `projectProductsPayload` chooses, and says why.
 */
function buildPatch(form: FormState, baseline: FormState): ProjectUpdateInput {
  const body: ProjectUpdateInput = {};

  // --- NOT NULL: changed => send, blanked => omit -------------------------
  const name = form.name.trim();
  if (name !== '' && name !== baseline.name.trim()) body.name = name;

  /*
   * ⚠️ The THREE product keys are diffed TOGETHER, in one place, and never here.
   * `productIds` alone cannot drop the primary — `replaceProducts` re-asserts it
   * — so removing the first chip would return a `200` and leave the product on
   * the project. `projectProductsBody` emits `productId` from the first member
   * in the same body, which moves the column first and stores exactly the set
   * the reader sees. Each half is still sent only when its own half moved.
   *
   * ⛔ And `projectProductsPayload` wraps it to pick between `productIds` and
   * `products`, WHICH MAY NEVER BOTH GO — a body carrying both is a 422 naming
   * both. The rule is written out there; it is the only thing in this form where
   * two write keys are alternatives rather than companions.
   */
  Object.assign(body, projectProductsPayload(productSelection(form), productSelection(baseline)));

  if (form.cycleId !== '' && form.cycleId !== baseline.cycleId) {
    body.cycleId = Number(form.cycleId);
  }

  if (form.isActive !== baseline.isActive) body.isActive = form.isActive;
  if (form.isVirtualEvent !== baseline.isVirtualEvent) {
    body.isVirtualEvent = form.isVirtualEvent;
  }

  // --- the four clearable fields -----------------------------------------
  if (form.intakeId !== baseline.intakeId) {
    body.intakeId = form.intakeId === '' ? null : Number(form.intakeId);
  }

  // ⚠️ Unlike {cycleId, intakeId}, the two dates are deliberately NOT sent as
  // a pair: the date check runs on the post-merge record too, but only ever
  // REFUSES, never silently re-validates a value the request can't see, so
  // `{startDate}` alone can legitimately come back 422 naming `endDate` — why
  // both keys are in `LABELS`. `null`, never `''`, on the clear (rule 2).
  if (form.startDate !== baseline.startDate) {
    body.startDate = form.startDate === '' ? null : form.startDate;
  }

  if (form.endDate !== baseline.endDate) {
    body.endDate = form.endDate === '' ? null : form.endDate;
  }

  // Each of the four figures diffed on its own: `''` -> null (not set) and
  // `'0'` -> 0 are two different outcomes. Never `capacity`.
  Object.assign(body, capacityPlanBody(form.capacityPlan, baseline.capacityPlan));

  const description = form.description.trim();
  if (description !== baseline.description.trim()) body.description = description;

  // --- the two SETS: a full replace each, sent only when one really moved ---
  // `[]` is how a set is cleared; `null` is a 422, and omission means
  // "untouched". All three intents are already expressible, which is why the
  // server refuses the fourth spelling.
  //
  // ⚠️ `regionIds` joined this block on 2026-08-29 (migration 095), replacing a
  // scalar `form.regionId !== baseline.regionId` compare a few lines above. The
  // diff matters more here than it reads: every member must be ACTIVE at write
  // time while a read resolves a retired region happily, so re-asserting an
  // untouched set that holds one is a 422 on a field nobody opened.
  if (!sameSet(form.regionIds, baseline.regionIds)) {
    body.regionIds = form.regionIds.map(Number);
  }

  if (!sameSet(form.cityIds, baseline.cityIds)) {
    body.cityIds = form.cityIds.map(Number);
  }

  // --- the pair rides together -------------------------------------------
  // `setCycle` already clears the intake on cycle change, so this only fires
  // when the user re-picked an intake from the new cycle's list.
  if (body.cycleId !== undefined && body.intakeId === undefined && form.intakeId !== '') {
    body.intakeId = Number(form.intakeId);
  }

  return body;
}

/**
 * Client-side mirror of the server's limits, for a faster message. Nothing is
 * checked here that the API does not also enforce — a stricter form would
 * reject values the API accepts.
 */
function validate(form: FormState): Record<string, string> {
  const errors: Record<string, string> = {};

  // The four per-column rules come from `./project-field-rules`, shared with
  // the inline editors on `/projects`. The two required-field checks below
  // stay here because they're about THIS form: an inline picker offers no
  // empty row for either column, so "you left it blank" is unreachable there.
  const name = validateProjectName(form.name);
  if (name) errors.name = name;

  /*
   * Keyed on `productIds`, the caption's own key, because that is the control a
   * reader can act on. The rule is shared with the inline editor rather than
   * spelled twice — and unlike the cycle below it is NOT merely a
   * required-field check: `[]` is a legal REQUEST that quietly keeps the primary
   * product, so refusing it here is what stops the screen and the record
   * disagreeing. See `validateProjectProductIds`.
   */
  const productIds = validateProjectProductIds(form.productIds);
  if (productIds) errors.productIds = productIds;

  // Required, and there is no "current cycle" the server falls back to.
  if (form.cycleId === '') errors.cycleId = 'Choose a cycle. Every project belongs to one.';

  for (const key of CAPACITY_PLAN_KEYS) {
    const message = validateCapacityPlanField(key, form.capacityPlan[key]);
    if (message) errors[key] = message;
  }

  // Each date is checked ALONE: `startDate > endDate` is the server's rule
  // (refusal lands on `endDate`, routing to a control), and the cycle window
  // is not a rule at all — a project may legitimately run outside its cycle's
  // dates, so a client-side check there would refuse a legitimate save with
  // nothing on screen explaining why.
  const startDate = validateProjectDate(form.startDate);
  if (startDate) errors.startDate = startDate;

  const endDate = validateProjectDate(form.endDate);
  if (endDate) errors.endDate = endDate;

  const description = validateProjectDescription(form.description);
  if (description) errors.description = description;

  const cityIds = validateProjectCityIds(form.cityIds);
  if (cityIds) errors.cityIds = cityIds;

  return errors;
}

export function ProjectFormModal({
  open,
  project,
  onClose,
  onSaved,
}: ProjectFormModalProps): React.JSX.Element | null {
  const record = project ?? null;
  const isEdit = record !== null;
  // Identity of the loaded record: changes for a different record *or* a newer
  // version of the same one, not merely when the parent re-renders.
  const recordKey = record ? `${record.id}:${record.updatedAt}` : 'new';

  const formId = useId();

  // ⚠️ The "add a new city" row is gated on the ROLE (`ability.isAdmin`), not
  // a permission: `POST /cities` is `ADMIN_ROLES` and there is no `cities.*`
  // permission to check — `/permissions/me` would answer a question nobody
  // asked. Reads on `/cities` are open to every signed-in role, so a
  // non-admin just doesn't see this one row.
  const { ability } = useAuth();

  const [form, setForm] = useState<FormState>(() => recordForm(record));
  const [baseline, setBaseline] = useState<FormState>(() => recordForm(record));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /* ---- option sources -------------------------------------------------- */

  const [products, setProducts] = useState<Product[]>([]);
  const [productsMeta, setProductsMeta] = useState({ truncated: false, total: 0, failed: false });
  const [productsLoaded, setProductsLoaded] = useState(false);

  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [, setCyclesMeta] = useState({ truncated: false, total: 0, failed: false });
  const [cyclesLoaded, setCyclesLoaded] = useState(false);

  const [cities, setCities] = useState<City[]>([]);
  const [citiesMeta, setCitiesMeta] = useState({ truncated: false, total: 0, failed: false });
  const [citiesLoaded, setCitiesLoaded] = useState(false);

  /** The "New city" dialog, opened from the pinned row in the cities picker. */
  const [addingCity, setAddingCity] = useState(false);
  /**
   * Cities created from inside this form, kept SEPARATE from `cities`.
   *
   * `cities` means precisely what `GET /cities?isActive=true` returned, and
   * `retiredSelected` reads it as exactly that — pushing a just-created row in
   * would be a lie for one created with Active unticked (the create form
   * allows it), which would then be sent and refused by the API naming an id
   * the form had just told the user was fine.
   *
   * So the two are merged where a LABEL is wanted (`cityOptions`) and only the
   * active ones count where ACTIVENESS is decided (`retiredSelected`).
   */
  const [createdCities, setCreatedCities] = useState<City[]>([]);

  // Regions come from the shared reference-options hook rather than a fetch
  // of their own because it already sends `?isActive=true` — `GET /regions`
  // unfiltered returns all 22 rows, 17 retired by migration 047, each a
  // guaranteed 422 at save time.
  const regions = useReferenceOptions('regions', open);

  /**
   * Intakes, tagged with the cycle they answer for — derived rather than
   * cleared in an effect, so "which cycle is this list for" and "which cycle
   * is selected" can never disagree for a frame (the frame where a user could
   * otherwise pick an intake from the previous cycle and get a 422).
   */
  const [intakeState, setIntakeState] = useState<{
    cycleId: number | null;
    intakes: Intake[];
    truncated: boolean;
    total: number;
    failed: boolean;
  }>({ cycleId: null, intakes: [], truncated: false, total: 0, failed: false });

  // Reopening must not show the previous record's values or the last
  // attempt's errors. Render-phase reset rather than an effect (an effect
  // paints the stale form for a frame first); keyed on `recordKey` rather
  // than object identity, so a parent re-render can't wipe the user's typing.
  const resetKey = `${open ? 'open' : 'closed'}|${recordKey}`;
  const [appliedKey, setAppliedKey] = useState(resetKey);
  if (appliedKey !== resetKey) {
    setAppliedKey(resetKey);
    if (open) {
      setForm(recordForm(record));
      setBaseline(recordForm(record));
      setErrors({});
      setFormError(null);
      setSaving(false);
      // Reset so a reopen shows the picker loading rather than the previous
      // sitting's list, which the effect below is about to replace anyway.
      setProductsLoaded(false);
      setProductsMeta({ truncated: false, total: 0, failed: false });
      setIntakeState({ cycleId: null, intakes: [], truncated: false, total: 0, failed: false });
      // The cities effect refetches on every open, so anything created in a
      // previous sitting comes back through `cities` itself.
      setCreatedCities([]);
      setAddingCity(false);
    }
  }

  /* ---- products: one page, filtered locally, active only ------------------
   *
   * `d_products` is the only one of the three catalog tables carrying an
   * active flag — hence only the product check refuses an inactive value at
   * write time. `?active=true` is a real server-side filter. A record's own
   * retired products are pinned back in below (see `productOptions`).
   *
   * ⚠️ **No `search` is sent, and that is a CHANGE from the single-select this
   * replaced** (migration 156). `MultiCombobox` has no `onSearch`, so a server
   * term could only ever narrow a list the control was already filtering
   * locally — and worse, it would make a live product that simply did not match
   * what was typed look absent from the active master, which is what
   * `productOptions`' "Retired" hint is decided on. The whole active page is
   * fetched instead and the control filters it, exactly as the cities picker
   * below does for the same reason. `productsMeta.truncated` is surfaced as the
   * picker's footnote so a list that outgrows the cap says so rather than
   * quietly hiding the product somebody is looking for.
   */
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    api
      .listProducts({ active: true, perPage: ENTITY_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setProducts(result.data);
        setProductsMeta({
          truncated: result.meta.totalPages > 1,
          total: result.meta.total,
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setProducts([]);
        setProductsMeta({ truncated: false, total: 0, failed: true });
      })
      .finally(() => {
        // Goes true on failure too, so "loading" ends and the failed state can
        // be reported — `productsMeta.failed` is what says which happened.
        if (!controller.signal.aborted) setProductsLoaded(true);
      });
    return () => controller.abort();
  }, [open]);

  /* ---- cycles: one page, filtered locally --------------------------------
   *
   * `/cycles` takes only `page`/`perPage` — no `search`, and an unknown query
   * param is stripped silently rather than rejected, so wiring `onSearch`
   * here would look like it worked while returning the same page every time.
   */
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    api
      .listCycles({ perPage: ENTITY_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCycles(result.data);
        setCyclesMeta({
          truncated: result.meta.totalPages > 1,
          total: result.meta.total,
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCycles([]);
        setCyclesMeta({ truncated: false, total: 0, failed: true });
      })
      .finally(() => {
        if (!controller.signal.aborted) setCyclesLoaded(true);
      });
    return () => controller.abort();
  }, [open]);

  /* ---- cities: active only, one page -------------------------------------
   *
   * `isActive` is a real server-side filter and every id in a write must be
   * active, so the picker is filtered there rather than locally.
   *
   * No `search` is sent: `MultiCombobox` has no `onSearch`, and its own filter
   * matches the option's label AND hint, so typing a country narrows the list
   * too — a server-side `search` could not, since it matches `name` alone.
   */
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    api
      .listCities({ isActive: true, perPage: CITIES_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCities(result.data);
        setCitiesMeta({
          truncated: result.meta.totalPages > 1,
          total: result.meta.total,
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCities([]);
        setCitiesMeta({ truncated: false, total: 0, failed: true });
      })
      .finally(() => {
        if (!controller.signal.aborted) setCitiesLoaded(true);
      });
    return () => controller.abort();
  }, [open]);

  /* ---- intakes: scoped to the selected cycle -----------------------------
   *
   * `cycleId` is a real server-side filter on `/intakes`, so the picker is
   * SCOPED rather than filtered: an intake outside the chosen cycle is never
   * offered. An unknown cycle id is an empty page, not an error.
   */
  const selectedCycleId = form.cycleId === '' ? null : Number(form.cycleId);

  useEffect(() => {
    if (!open || selectedCycleId === null) return;
    const controller = new AbortController();
    api
      .listIntakes({ cycleId: selectedCycleId, perPage: ENTITY_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setIntakeState({
          cycleId: selectedCycleId,
          intakes: result.data,
          truncated: result.meta.totalPages > 1,
          total: result.meta.total,
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setIntakeState({
          cycleId: selectedCycleId,
          intakes: [],
          truncated: false,
          total: 0,
          failed: true,
        });
      });
    return () => controller.abort();
  }, [open, selectedCycleId]);

  const intakesLoading = selectedCycleId !== null && intakeState.cycleId !== selectedCycleId;
  const intakesReady = selectedCycleId !== null && intakeState.cycleId === selectedCycleId;

  const productsLoading = open && !productsLoaded;
  const cyclesLoading = open && !cyclesLoaded;
  const citiesLoading = open && !citiesLoaded;

  /* ---- option lists ----------------------------------------------------- */

  /**
   * The active products, plus EVERY product the record already holds pinned
   * back in when it has since been retired. Without the pin a retired member
   * renders as a bare id and the next save looks like a deliberate change; with
   * it, since the patch only sends `productIds` when the SET moved, an untouched
   * retired member is never re-asserted and never refused.
   *
   * ⚠️ It walks `record.products`, not the deprecated `record.product` — pinning
   * only the primary would leave a retired SECONDARY, which is the whole point
   * of migration 156, as an unlabelled id in a chip.
   *
   * ⚠️ The pin is unconditional; only the "Retired" REASON is gated, withheld
   * while the fetch is in flight or failed (`products` is `[]`, so every stored
   * product looks absent). The option still shows, just with no explanation:
   * saying less beats saying something wrong. The search-term branch this
   * condition used to carry is gone with the server-side search itself.
   */
  const productOptions = useMemo<MultiComboboxOption[]>(() => {
    const options: MultiComboboxOption[] = products.map((product) => ({
      value: String(product.id),
      // `productName`, not `.name` — the catalog keys differ from the CRM ones.
      label: product.productName,
      hint: product.productCode ?? undefined,
    }));
    const listIsTheActiveMaster = productsLoaded && !productsMeta.failed;
    const known = new Set(options.map((option) => option.value));
    for (const stored of record?.products ?? []) {
      const value = String(stored.id);
      if (known.has(value)) continue;
      known.add(value);
      options.push({
        value,
        label: stored.name,
        ...(listIsTheActiveMaster ? { hint: 'Retired' } : {}),
      });
    }
    return options;
  }, [products, record, productsLoaded, productsMeta.failed]);

  /**
   * Selected product ids the active master does not list — every one a 422 at
   * write time, on `productIds` or on `productId` depending which it is.
   *
   * ⚠️ A failed or unread master answers `[]`, not "all of them" — the same rule
   * `retiredSelected` applies to cities, and for the same reason: this becomes a
   * blocking field error, so an unreadable list would refuse the save while
   * telling the user their live products are retired.
   */
  const retiredProductsSelected = useMemo(() => {
    if (!productsLoaded || productsMeta.failed) return [] as string[];
    const active = new Set(products.map((product) => String(product.id)));
    return form.productIds.filter((id) => !active.has(id));
  }, [productsLoaded, productsMeta.failed, products, form.productIds]);

  const labelOfProduct = useCallback(
    (id: string) => productOptions.find((option) => option.value === id)?.label ?? id,
    [productOptions],
  );

  const cycleOptions = useMemo<ComboboxOption[]>(() => {
    const options: ComboboxOption[] = cycles.map((cycle) => ({
      value: String(cycle.id),
      // `cycleName`, not `.name`.
      label: cycle.cycleName,
      // Plain 'YYYY-MM-DD' strings, shown verbatim — never parsed to a Date.
      hint: `${cycle.startDate} – ${cycle.endDate}`,
    }));
    // `d_cycles` has no active flag, so a stored cycle can only be missing here
    // because it fell off the page — pin it either way.
    const stored = record?.cycle;
    if (stored && !options.some((option) => option.value === String(stored.id))) {
      options.unshift({ value: String(stored.id), label: stored.name });
    }
    return options;
  }, [cycles, record]);

  /**
   * Only the selected cycle's intakes, and the stored intake is pinned ONLY
   * while the cycle is still the stored one. Pinning it across a cycle change
   * would offer a choice that is a guaranteed 422.
   */
  const intakeOptions = useMemo<ComboboxOption[]>(() => {
    if (!intakesReady) return [];
    const options: ComboboxOption[] = intakeState.intakes.map((intake) => ({
      value: String(intake.id),
      // `intakeName`, not `.name`.
      label: intake.intakeName,
      hint: `${intake.startDate} – ${intake.endDate}`,
    }));
    const stored = record?.intake;
    if (
      stored &&
      form.cycleId === baseline.cycleId &&
      !options.some((option) => option.value === String(stored.id))
    ) {
      options.unshift({ value: String(stored.id), label: stored.name });
    }
    return options;
  }, [intakesReady, intakeState.intakes, record, form.cycleId, baseline.cycleId]);

  /**
   * The region picker's options — every ACTIVE region, plus any the record
   * already holds that are not among them.
   *
   * ⚠️ **This was `withStoredOption(regions.records, record?.region)` feeding a
   * single-valued `Combobox`.** Migration 095 made a project's regions a set,
   * so it now shares the COMPANY surface's helper: a project's `regionIds` and
   * a company's are the same contract, the backend shaped them that way
   * deliberately, and a second implementation of one rule would agree on the
   * day it was written and drift on the first edit.
   *
   * The deactivation trap is the same either way. The read carries no active
   * filter, so a project on a since-retired region still reads its name, while
   * re-sending that id is a 422 — and because `regionIds` is a whole-set
   * REPLACE, a stored id with no chip to represent it would simply be dropped by
   * the next save of something else: a **200**, with nothing on screen having
   * said so. Injecting it keeps the id in the draft unless a person removes it;
   * the diff in `buildPatch` and the refusal in `handleSubmit` are the other two
   * parts, and all three are required.
   */
  const regionOptions = useMemo<MultiComboboxOption[]>(
    () => regionPickerOptions(regions.records, record?.regions ?? []),
    [regions.records, record],
  );

  /**
   * Selected region ids that are not in the active list.
   *
   * ⚠️ **A failed or unread master answers `[]`, not "all of them"** — the same
   * rule `retiredSelected` applies to cities, and for the same reason:
   * `handleSubmit` turns this into a blocking field error, so an unreadable
   * master would refuse the save while telling the user their live regions are
   * retired. `retiredRegionNames` already withholds on an empty `records`, and
   * this maps the names back to the ids the chips are keyed on.
   */
  const retiredRegionsSelected = useMemo(() => {
    if (regions.records.length === 0) return [] as string[];
    const active = new Set(regions.records.map((region) => String(region.id)));
    return form.regionIds.filter((id) => !active.has(id));
  }, [regions.records, form.regionIds]);

  const labelOfRegion = useCallback(
    (id: string) => regionOptions.find((option) => option.value === id)?.label ?? id,
    [regionOptions],
  );

  /**
   * The active cities, plus any the record already holds that have since been
   * retired — merged in so the chip shows a name rather than a bare id, marked
   * because a write containing one is a 422.
   *
   * ⚠️ The merge is unconditional; only the "Retired" REASON is gated. An
   * unread list is `[]`, so every stored city would be absent from it —
   * marking them all retired would accuse perfectly live cities on the
   * strength of a request that never came back. Unread, the chip shows its
   * name and says nothing; `citiesHint` already names the failure once.
   *
   * The country is part of the LABEL, not the hint: city names are ambiguous
   * across countries (unique key is `(name, countryId)`, `NULL`s count as
   * distinct, so two "Cambridge" rows with no country are legal) and the
   * trigger renders chips from the label alone — a country in the hint would
   * disambiguate the open list and then vanish from the chosen chip.
   */
  const cityOptions = useMemo<MultiComboboxOption[]>(() => {
    const options: MultiComboboxOption[] = cities.map((city) => ({
      value: String(city.id),
      label: city.country ? `${city.name} · ${city.country.name}` : city.name,
      // Geography is optional on the cities master, and saying so is better
      // than an unexplained gap where every neighbour has a country.
      hint: city.country ? undefined : 'No country recorded',
    }));
    const listIsTheActiveMaster = citiesLoaded && !citiesMeta.failed;
    const known = new Set(options.map((option) => option.value));
    // Cities created from the picker's own "add a new city" row. A retired
    // one is labelled here too — the create form allows creating one retired,
    // and `handleSubmit` then refuses the save naming it.
    for (const created of createdCities) {
      if (known.has(String(created.id))) continue;
      known.add(String(created.id));
      options.push({
        value: String(created.id),
        label: created.country ? `${created.name} · ${created.country.name}` : created.name,
        ...(created.isActive
          ? created.country
            ? {}
            : { hint: 'No country recorded' }
          : { hint: 'Retired' }),
      });
    }
    for (const stored of record?.cities ?? []) {
      if (!known.has(String(stored.id))) {
        options.push({
          value: String(stored.id),
          label: stored.country ? `${stored.name} · ${stored.country.name}` : stored.name,
          ...(listIsTheActiveMaster ? { hint: 'Retired' } : {}),
        });
      }
    }
    return options;
  }, [cities, createdCities, record, citiesLoaded, citiesMeta.failed]);

  /**
   * Selected ids that are not in the active list. Reads resolve a retired
   * city happily; writes do not — so this only matters once the set is
   * actually being sent, which is exactly the check `handleSubmit` makes.
   *
   * ⚠️ A failed fetch answers `[]`, not "all of them": `citiesLoaded` goes
   * true in the `.finally` even on failure, so `cities` is `[]` and every
   * selected city would be reported retired — and `handleSubmit` turns this
   * into a blocking field error. Withheld on failure, so the write instead
   * goes to the server and gets a truthful 422 naming the real ids.
   * `citiesHint` already says the list could not be read.
   */
  const retiredSelected = useMemo(() => {
    if (!citiesLoaded || citiesMeta.failed) return [] as string[];
    const active = new Set(cities.map((city) => String(city.id)));
    // A created-and-retired city is deliberately NOT added here, so it is
    // reported exactly as the API will refuse it.
    for (const created of createdCities) {
      if (created.isActive) active.add(String(created.id));
    }
    return form.cityIds.filter((id) => !active.has(id));
  }, [citiesLoaded, citiesMeta.failed, cities, createdCities, form.cityIds]);

  const labelOfCity = useCallback(
    (id: string) => cityOptions.find((option) => option.value === id)?.label ?? id,
    [cityOptions],
  );

  /* ---- derived ---------------------------------------------------------- */

  // Derived from the payload itself, so "Save is enabled" and "we have
  // something to send" can never disagree — a PATCH {} is a guaranteed 422.
  const patch = useMemo(
    () => (isEdit ? buildPatch(form, baseline) : null),
    [isEdit, form, baseline],
  );
  const dirty = patch === null || Object.keys(patch).length > 0;

  // Blanking a NOT-NULL field produces no patch key (`null` on those is a
  // 422), so the diff stays empty and Save would lock over a visibly empty
  // required box. Let submit through instead so `validate` reports it; the
  // empty-patch guard in `handleSubmit` still stops a `{}` reaching the API.
  const requiredBlank =
    form.name.trim() === '' || form.productIds.length === 0 || form.cycleId === '';
  const submitDisabled = isEdit && !dirty && !requiredBlank;

  const citiesChanged = !sameSet(form.cityIds, baseline.cityIds);
  const regionsChanged = !sameSet(form.regionIds, baseline.regionIds);
  /*
   * ⚠️ NOT `sameSet` — this one is positional, because the two product keys are
   * not one set. Re-picking the same products in a different order leaves the
   * membership alone and MOVES THE PRIMARY, which is a real write (`productId`)
   * that `sameSet` reports as no change. On a create everything is new.
   */
  const productsChanged =
    !isEdit ||
    form.productIds.length !== baseline.productIds.length ||
    form.productIds.some((id, index) => id !== baseline.productIds[index]) ||
    /*
     * ⚠️ A TICK-ONLY change sends `products`, which re-asserts EVERY member —
     * including a since-retired one, which refuses the whole write. So the
     * retired-product check has to fire here too, on an edit where both compares
     * above see nothing move. Without this, unticking a box on a project holding
     * a retired product would send a payload that is a guaranteed 422, with the
     * form having said nothing about the chip that caused it.
     *
     * ⚠️ Asked of the PAYLOAD rather than re-derived here: the tick diff is
     * private to `./project-field-rules`, and "will the next save carry
     * `products`" is the question this line actually has. It answers yes for a
     * MEMBERSHIP move as well, which adds nothing to this disjunction — a set
     * that moved cannot be positionally equal, so one of the two compares above
     * has already returned true — so the result is the same whatever order these
     * operands are read in.
     */
    projectProductsPayload(productSelection(form), productSelection(baseline)).products !== undefined;

  /* ---- handlers --------------------------------------------------------- */

  function clearError(key: string) {
    setErrors((previous) => {
      if (!(key in previous)) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
    clearError(key as string);
  }

  /** One of the four capacity figures; its error is keyed on the write key. */
  function setCapacityFigure(key: CapacityPlanKey, value: string) {
    setForm((previous) => ({ ...previous, capacityPlan: { ...previous.capacityPlan, [key]: value } }));
    clearError(key);
  }

  /**
   * The shared date rule, run on blur so a bad value is named where typed
   * rather than at submit. Fires rarely through a native picker (which hands
   * back `''` for anything unparseable) but the control degrades to a plain
   * text box on some browsers, and a paste is not a pick. `validate` reruns
   * the same check on submit, so this is the earlier report, never the only one.
   */
  function checkDate(key: 'startDate' | 'endDate', value: string) {
    const message = validateProjectDate(value);
    if (message) setErrors((previous) => ({ ...previous, [key]: message }));
  }

  /**
   * THE LOAD-BEARING ONE. Changing the cycle clears the intake — the server
   * judges the pair against the record as it will stand after the patch, so a
   * cycle move that leaves the old intake in place comes back 422 naming
   * `intakeId`, a field the user never touched. Clearing it here makes that
   * state unreachable: the intake picker refetches for the new cycle, and the
   * diff sends `intakeId: null` alongside `cycleId` in the same request.
   */
  /**
   * ⚠️ Clears BOTH product keys' errors, for the same reason `setCycle` clears
   * two: one control emits two write keys, so a 422 that named `productId` must
   * not survive an edit the reader has plainly made to fix it — it would sit
   * under the control looking live while describing a value that has moved.
   */
  function setProductIds(values: string[]) {
    setForm((previous) => ({ ...previous, productIds: values }));
    clearError('productIds');
    clearError('productId');
    // A pick can change which set key the next save uses — see below.
    clearError('products');
  }

  /**
   * One product's tick.
   *
   * ⚠️ Clears ALL THREE product errors, for a reason `setProductIds` only half
   * had: a tick decides WHICH WRITE KEY the next save carries, so a 422 that
   * named `productIds` can be answered by a body naming `products`, and the
   * other way round. A message left standing under the control would describe a
   * key the request no longer sends — live-looking, and about a value that has
   * moved.
   *
   * Writes the entry rather than toggling a derived value, so a product whose
   * flag was only ever the default gains a real one the moment it is touched and
   * the tick diff inside `projectProductsPayload` can see it.
   */
  function setSeedsLineItems(productId: string, next: boolean) {
    setForm((previous) => ({
      ...previous,
      seedsLineItems: { ...previous.seedsLineItems, [productId]: next },
    }));
    clearError('productIds');
    clearError('productId');
    clearError('products');
  }

  function setCycle(value: string) {
    setForm((previous) =>
      previous.cycleId === value ? previous : { ...previous, cycleId: value, intakeId: '' },
    );
    clearError('cycleId');
    clearError('intakeId');
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // `name` is not unique on this resource, so a second submit creates a
    // second project rather than failing.
    if (saving) return;

    const clientErrors = validate(form);

    /*
     * Only a problem when the set is actually being sent: a retired region
     * reads back fine, so an untouched list is never re-asserted. Same shape as
     * the cities check below it, because it is the same contract — `regionIds`
     * is validated as a SET, so one dead member refuses the whole write and the
     * honest sentence is "this cannot be saved until the chip is removed", not
     * "it will be dropped".
     */
    const sendingRegions = !isEdit ? form.regionIds.length > 0 : regionsChanged;
    if (!clientErrors.regionIds && sendingRegions && retiredRegionsSelected.length > 0) {
      clientErrors.regionIds = `${retiredRegionsSelected
        .map(labelOfRegion)
        .join(', ')} ${retiredRegionsSelected.length === 1 ? 'is' : 'are'} no longer active, and the API refuses a region list containing a retired region. Remove ${retiredRegionsSelected.length === 1 ? 'it' : 'them'} to save this change.`;
    }

    /*
     * ⚠️ Products have NO "only when sent" escape, unlike the two sets above.
     * On an edit the primary is re-validated whenever `productId` moves and the
     * set whenever it moves, but the two keys travel together — and on a CREATE
     * both always go. More to the point the check is cheap and the refusal is
     * the same either way: a retired member is a 422 whichever key names it, so
     * a stored one has to come out before anything on this control can be saved.
     */
    if (!clientErrors.productIds && productsChanged && retiredProductsSelected.length > 0) {
      clientErrors.productIds = `${retiredProductsSelected
        .map(labelOfProduct)
        .join(', ')} ${retiredProductsSelected.length === 1 ? 'is' : 'are'} no longer active, and the API refuses a product list containing a retired product. Remove ${retiredProductsSelected.length === 1 ? 'it' : 'them'} to save this change.`;
    }

    // Only a problem when the set is actually being sent: a retired city reads
    // back fine, so an untouched list is never re-asserted.
    const sendingCities = !isEdit ? form.cityIds.length > 0 : citiesChanged;
    if (!clientErrors.cityIds && sendingCities && retiredSelected.length > 0) {
      clientErrors.cityIds = `${retiredSelected
        .map(labelOfCity)
        .join(', ')} ${retiredSelected.length === 1 ? 'is' : 'are'} no longer active, and the API refuses a city list containing a retired city. Remove ${retiredSelected.length === 1 ? 'it' : 'them'} to save this change.`;
    }

    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFormError(null);
      return;
    }

    const body = patch ?? buildCreate(form);
    // Belt and braces: the button is disabled in this state, but an empty patch
    // is a guaranteed 422 and must never leave the client.
    if (isEdit && Object.keys(body).length === 0) return;

    setSaving(true);
    setErrors({});
    setFormError(null);

    try {
      const result = record
        ? await api.updateProject(record.id, body)
        : await api.createProject(body as ProjectCreateInput);
      onSaved(result.data);
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        // `mapped` keys a control here — e.g. the pair check can name
        // `intakeId` on a request that only carried `cycleId`, and that
        // message is rendered verbatim against the intake control. `unmapped`
        // is everything else (empty-path refusals and any path naming a key
        // with no control here) and can only be shown at form level.
        const { fields: mapped, unroutable: unmapped } = error.routeFieldErrors(KNOWN_FIELDS);
        setErrors(mapped);

        const extras = unmapped.filter((line) => line !== error.message);

        if (error.status === 403) {
          // A 403 carries NO `details`, so it can only ever be a banner.
          setFormError(
            'You do not have permission to save projects. Creating and editing needs a write role.',
          );
        } else if (error.status === 404) {
          setFormError('This project no longer exists.');
        } else if (extras.length > 0) {
          setFormError(`${error.message} — ${extras.join('; ')}`);
        } else if (unmapped.length > 0) {
          setFormError(error.message);
        } else if (Object.keys(mapped).length > 0) {
          setFormError(null);
        } else {
          setFormError(error.message);
        }
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  /* ---- render helpers --------------------------------------------------- */

  const citiesHint = (() => {
    if (retiredSelected.length > 0) {
      const names = retiredSelected.map(labelOfCity).join(', ');
      return `${names} ${retiredSelected.length === 1 ? 'is' : 'are'} retired — any change to this list has to drop ${retiredSelected.length === 1 ? 'it' : 'them'}.`;
    }
    // A permission denial: writes to /cities are admin-only, so a non-admin
    // really would be refused. The admin-facing half of this branch was advice
    // and went with the 2026-09-05 sweep.
    // 2026-10-01: cities are a `reference-data` master and the set decides.
    if (citiesLoaded && cityOptions.length === 0 && !ability.canCreate('reference-geography')) {
      return 'No cities recorded, and your permission set does not include adding one.';
    }
    return 'Saving replaces the whole list; only these cities may be named on a line.';
  })();

  /*
   * ⛔ Not helper text — a REFUSAL: this field is unsaveable until the retired
   * chip comes out. The failed-read branch beside it went with the 2026-09-05
   * sweep.
   */
  const regionHint = (() => {
    if (retiredRegionsSelected.length > 0) {
      const names = retiredRegionsSelected.map(labelOfRegion).join(', ');
      return `${names} ${retiredRegionsSelected.length === 1 ? 'is' : 'are'} retired — remove ${retiredRegionsSelected.length === 1 ? 'it' : 'them'} to save this field.`;
    }
    return undefined;
  })();

  /*
   * ⛔ Not helper text, and nothing here explains the control. Two branches,
   * each a REFUSAL naming a state the reader cannot otherwise see: a retired
   * chip that makes the whole field unsaveable, and a list that never arrived —
   * which matters more on this field than on cities, because products cannot be
   * created from here, so an unreadable master leaves no way to fix a selection.
   */
  const productsHint = (() => {
    if (retiredProductsSelected.length > 0) {
      const names = retiredProductsSelected.map(labelOfProduct).join(', ');
      return `${names} ${retiredProductsSelected.length === 1 ? 'is' : 'are'} retired — remove ${retiredProductsSelected.length === 1 ? 'it' : 'them'} to save this field.`;
    }
    if (productsLoaded && productsMeta.failed) {
      return 'Products could not be loaded.';
    }
    return undefined;
  })();

  /*
   * ⚠️ JOINED, never `??`. `productId`, `productIds` and `products` are three
   * write keys behind one control and a single 422 can name more than one — the
   * primary being inactive and a member being inactive are separate refusals in
   * the same response. Coalescing would show one and silently drop the other,
   * which is this codebase's most-repeated defect.
   *
   * ⚠️ `productIds` and `products` are alternatives on the WIRE and are NOT
   * alternatives here: a stale message from the previous attempt can still be
   * sitting under the control when the reader's edit switches which key the next
   * save uses, so both slots are read. `setSeedsLineItems` clears all three for
   * the same reason from the other end.
   */
  const productsError =
    [errors.productIds, errors.productId, errors.products]
      .filter((message) => message !== undefined)
      .join(' ') || undefined;

  /*
   * A FACT about the data, not advice: the list is one page and the control
   * filters it locally, so a master past the cap would silently hide the product
   * somebody is hunting for. Unreachable today (28 products against a cap of
   * 100) and stated the day it is not.
   */
  const productsFootnote = productsMeta.truncated
    ? `Showing the first ${ENTITY_PER_PAGE} of ${productsMeta.total}.`
    : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      title={isEdit ? 'Edit project' : 'New project'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            loading={saving}
            disabled={submitDisabled}
          >
            {isEdit ? 'Save changes' : 'Create project'}
          </Button>
        </>
      }
    >
      <form id={formId} noValidate onSubmit={handleSubmit} className="grid min-w-0 gap-3">
        {formError && (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        )}

        <Section title="Essentials">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label={LABELS.name} required error={errors.name}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    name="name"
                    value={form.name}
                    maxLength={NAME_MAX}
                    autoComplete="off"
                    disabled={saving}
                    invalid={Boolean(errors.name)}
                    onChange={(event) => setField('name', event.target.value)}
                  />
                )}
              </Field>
            </div>

            {/*
              The picker and the per-product ticks are ONE block spanning both
              columns, rather than the picker sharing a row with Capacity as it
              did before the ticks existed. They answer one question in two
              steps — which products, then which of those start a line — so a
              control between them is a control read in the middle of a sentence,
              and at 320 (one column) it would put Capacity between them
              outright. The four capacity figures follow as their own block.
            */}
            <div className="grid min-w-0 gap-3 sm:col-span-2">
              {/*
                ⚠️ ONE control for THREE write keys, captioned `productIds`. A
                422 can name any of them — `productId` for the primary,
                `productIds` or `products` for a member — and all of them have to
                land here, because this is the only control a reader can act on.
                `productsError` JOINS them rather than coalescing: several can be
                true at once (an inactive primary AND an inactive secondary are
                two separate refusals in one response), and `a ?? b` between two
                live messages drops one silently.
              */}
              <Field label={LABELS.productIds} required hint={productsHint} error={productsError}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <MultiCombobox
                    id={id}
                    aria-describedby={describedBy}
                    values={form.productIds}
                    onChange={setProductIds}
                    options={productOptions}
                    // ⚠️ Not "Select a product…" — the placeholder describes the
                    // EMPTY state, and empty is a state this form refuses rather
                    // than one it can save. It names what is missing.
                    placeholder="No products chosen"
                    searchPlaceholder="Filter products…"
                    loading={productsLoading && productOptions.length === 0}
                    disabled={saving}
                    maxSelected={PRODUCTS_MAX}
                    summaryNoun={{ singular: 'product', plural: 'products' }}
                    /*
                     * ⚠️ **Suppressed exactly when `productsFootnote` is set, and
                     * that is the point rather than a gap.** The footnote on this
                     * mount reports a TRUNCATED product list, and "select all"
                     * over a list the server cut short would claim to have
                     * selected products it never showed. When the list is whole —
                     * the ordinary case — the row appears.
                     */
                    selectAll
                    {...(productsFootnote ? { footnote: productsFootnote } : {})}
                    invalidValues={retiredProductsSelected}
                    invalid={Boolean(productsError)}
                  />
                )}
              </Field>

              {/*
                Which of the chosen products the company "+" flow pulls into a
                new document's line items. Owner's instruction, 2026-09-13: that
                flow seeded EVERY product on the project, and this is the control
                that replaces the hardcoding.

                ⛔ **There is no helper text here and none may be added.** The
                group's LEGEND is the ticks' label — a checkbox's label is what a
                checkbox MEANS, every box in the group is announced with it, and
                it is not a sentence sitting under a control. Strip it and each
                tick says only WHICH product, never what ticking does; move it to
                a `description` on each `Checkbox` and the same sentence repeats
                once per product, under every box, which is exactly the
                prose-attached-to-a-control this repo forbids.

                ⚠️ **The whole group is ABSENT until something is chosen**, not
                rendered empty: unchosen products have nothing to tick, and a
                legend promising a consequence over no boxes at all reads as a
                list that failed to load.

                One box per CHOSEN product, in `productIds` order — primary
                first, the same order as the chips above, so the two are read
                together rather than cross-referenced.
              */}
              {form.productIds.length > 0 && (
                <fieldset className="min-w-0">
                  <legend className="mb-1.5 text-xs font-medium text-fg-secondary">
                    Added as a line item on a new quote, invoice or deal
                  </legend>
                  {/*
                    One column below `sm` — the breakpoint the rest of this form
                    collapses at — so this block is a single stack at 320 like
                    every other control here. `min-w-0` on both the fieldset and
                    the track because a long product name is a wide flex child
                    and a fieldset's default min-inline-size is `min-content`.
                  */}
                  <div className="grid min-w-0 gap-2 sm:grid-cols-2">
                    {form.productIds.map((id) => (
                      <Checkbox
                        key={id}
                        checked={seedsLineItemsOf(form.seedsLineItems, id)}
                        onChange={(event) => setSeedsLineItems(id, event.target.checked)}
                        disabled={saving}
                        // The product's own name IS this box's label, so the
                        // group's legend and the box read as one sentence. No
                        // `aria-label`: it would REPLACE the visible text rather
                        // than add to it, and a control whose accessible name
                        // omits its own visible label is the label-in-name
                        // failure.
                        label={labelOfProduct(id)}
                      />
                    ))}
                  </div>
                </fieldset>
              )}
            </div>

            {/*
              Four figures in two rows at `sm`+ — Institution (planned capacity,
              target), then Partner — one column at 320. "Uncapped" sits only on
              the planned-capacity boxes; a target box has no placeholder.
            */}
            <div className="grid min-w-0 gap-3 sm:col-span-2 sm:grid-cols-2">
              {CAPACITY_PLAN_KEYS.map((key) => (
                <Field key={key} label={LABELS[key]} error={errors[key]}>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      name={key}
                      inputMode="numeric"
                      value={form.capacityPlan[key]}
                      {...(key === 'institutionCapacity' || key === 'partnerCapacity'
                        ? { placeholder: 'Uncapped' }
                        : {})}
                      autoComplete="off"
                      disabled={saving}
                      invalid={Boolean(errors[key])}
                      onChange={(event) => setCapacityFigure(key, event.target.value)}
                    />
                  )}
                </Field>
              ))}
            </div>

          </div>
        </Section>

        <Section title="Period">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={LABELS.cycleId} required error={errors.cycleId}>
              {({ id, 'aria-describedby': describedBy }) => (
                // No `onSearch`: /cycles takes only page and perPage, and an
                // unknown query param is stripped silently, so a server search
                // here would look like it worked. The Combobox filters the page.
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.cycleId}
                  onChange={setCycle}
                  options={cycleOptions}
                  placeholder="Select a cycle…"
                  searchPlaceholder="Filter cycles…"
                  loading={cyclesLoading && cycleOptions.length === 0}
                  disabled={saving}
                  invalid={Boolean(errors.cycleId)}
                />
              )}
            </Field>

            <Field label={LABELS.intakeId} error={errors.intakeId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.intakeId}
                  onChange={(value) => setField('intakeId', value)}
                  options={intakeOptions}
                  placeholder={
                    selectedCycleId === null
                      ? 'Choose a cycle first'
                      : 'The whole cycle — no tracking'
                  }
                  // The empty row is NAMED rather than blank, and it carries
                  // Xero's own word for it. There is no `d_intakes` row called
                  // "No tracking" and there must not be — see the tracking
                  // block at the top of this file. This is `intakeId: null`.
                  emptyOptionLabel="The whole cycle — no tracking"
                  searchPlaceholder="Filter intakes…"
                  loading={intakesLoading}
                  disabled={saving || selectedCycleId === null}
                  invalid={Boolean(errors.intakeId)}
                />
              )}
            </Field>

            <p className="text-xs text-fg-muted sm:col-span-2">
              Also the Xero <strong className="font-medium text-fg-secondary">Tour Type</strong>{' '}
              these lines report into. Left unset, they drop out of that reporting silently.
            </p>

            {/*
              When the project RUNS — a different question from the cycle and
              intake above (the commercial period it's grouped under; a
              project may legitimately run outside them, and nothing compares
              the two). Both dates are optional and independent.
            */}
            <Field label={LABELS.startDate} error={errors.startDate}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="startDate"
                  type="date"
                  value={form.startDate}
                  disabled={saving}
                  invalid={Boolean(errors.startDate)}
                  onChange={(event) => setField('startDate', event.target.value)}
                  onBlur={(event) => checkDate('startDate', event.target.value)}
                />
              )}
            </Field>

            <Field label={LABELS.endDate} error={errors.endDate}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="endDate"
                  type="date"
                  value={form.endDate}
                  /*
                   * The picker's own floor, not a validation rule. The form is
                   * `noValidate` and this constrains nothing that is typed or
                   * pasted — `startDate > endDate` stays the server's refusal,
                   * arrives with `path: "endDate"`, and marks this control.
                   */
                  min={form.startDate || undefined}
                  disabled={saving}
                  invalid={Boolean(errors.endDate)}
                  onChange={(event) => setField('endDate', event.target.value)}
                  onBlur={(event) => checkDate('endDate', event.target.value)}
                />
              )}
            </Field>
          </div>
        </Section>

        <Section title="Where it runs">
          <div className="grid min-w-0 gap-3">
            <Field label={LABELS.regionIds} hint={regionHint} error={errors.regionIds}>
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  aria-describedby={describedBy}
                  values={form.regionIds}
                  onChange={(values) => setField('regionIds', values)}
                  options={regionOptions}
                  // An EMPTY set is the "No tracking" state, and unlike the
                  // intake row above there is no empty OPTION to name it with:
                  // clearing a multi-select is removing the last chip. The
                  // placeholder is what carries the name, in both vocabularies.
                  // A `f_regions` row literally named "No tracking" would be a
                  // guaranteed XERO_TRACKING_LOCATION_UNKNOWN on every push.
                  placeholder="Unclassified — no tracking"
                  searchPlaceholder="Filter regions…"
                  loading={regions.loading && regionOptions.length === 0}
                  disabled={saving}
                  // Form-staged, so a select-all is reviewed before it is saved
                  // and undone by unticking. ⚠️ It is NOT harmless here: ONE
                  // region becomes the Xero tracking Location and TWO OR MORE
                  // push with NO Location, SILENTLY — so selecting all can
                  // remove a document's Location with nothing failing. Kept
                  // because this is a form, not a live per-toggle cell.
                  selectAll
                  invalid={Boolean(errors.regionIds)}
                />
              )}
            </Field>

            <Field label={LABELS.cityIds} hint={citiesHint} error={errors.cityIds}>
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  aria-describedby={describedBy}
                  values={form.cityIds}
                  onChange={(values) => setField('cityIds', values)}
                  options={cityOptions}
                  placeholder="No cities"
                  searchPlaceholder="Search cities…"
                  loading={citiesLoading && cityOptions.length === 0}
                  disabled={saving}
                  maxSelected={CITIES_MAX}
                  // Form-staged: reviewed before saving, so a select-all is
                  // offered. ⚠️ The PRODUCTS picker above deliberately has no
                  // `selectAll` — it passes a truncation `footnote`, and the
                  // component suppresses the row whenever one is present, so
                  // the prop would be inert there rather than merely unwanted.
                  selectAll
                  // Admins only, and withheld over a list that failed to load.
                  // ⚠️ The second reason is no longer stated anywhere on screen
                  // — its sentence went with the 2026-09-05 sweep.
                  addNew={
                    ability.canCreate('reference-geography') && !citiesMeta.failed
                      ? { label: '+ Add a new city…', onSelect: () => setAddingCity(true) }
                      : undefined
                  }
                  invalid={Boolean(errors.cityIds)}
                />
              )}
            </Field>

            {/*
              The Location half of the tracking story. Stated here, not in the
              field hint, since it's three facts and a hint is replaced
              wholesale by an error.

              The middle sentence is the one that is easy to get wrong: tracking
              falls back PER PROJECT, not per field. A line that names a project
              uses that project whole, so an unset region here is not filled in
              from the region on the document's own project — see
              `resolveLineTracking` in the backend's xero-quote.payload.ts.

              No region name is hardcoded. `Latin America` is the only active
              region with no Xero Location option today, but that is master data
              a person can rename — matching on it here would be the same
              mistake as matching a refusal on its message text.

              ⚠️ **Reworded 2026-08-29 for migration 095, and one sentence was
              DELETED rather than pluralised.** It read "A line naming this
              project takes its Location from this project's REGION ALONE",
              which was a true statement about a single-valued field. With a SET
              it would be a claim about which member becomes the one Xero
              `Location`, and **nothing here has measured that** — the tracking
              resolver was written against `project.region`. Copy is the one part
              of a contract nothing typechecks, so the paragraph now says only
              what is still known: the regions decide the Location, an empty set
              means no Location, and an unmatched name refuses the push. **Ask
              the backend which member wins before writing a sentence about it.**
            */}
            <p className="text-xs text-fg-muted">
              Also the Xero <strong className="font-medium text-fg-secondary">Location</strong>{' '}
              these lines report into. Empty means no Location; a name Xero does not have refuses
              the push.
            </p>
          </div>
        </Section>

        <Section title="Description">
          <Field label={LABELS.description} hideLabel error={errors.description}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                name="description"
                rows={4}
                value={form.description}
                maxLength={DESCRIPTION_MAX}
                disabled={saving}
                invalid={Boolean(errors.description)}
                onChange={(event) => setField('description', event.target.value)}
              />
            )}
          </Field>
        </Section>

        <Checkbox
          checked={form.isActive}
          onChange={(event) => setField('isActive', event.target.checked)}
          disabled={saving}
          label={LABELS.isActive}
        />

        <Checkbox
          checked={form.isVirtualEvent}
          onChange={(event) => setField('isVirtualEvent', event.target.checked)}
          disabled={saving}
          label={LABELS.isVirtualEvent}
        />
      </form>

      {/*
        MOUNTED ONLY WHILE OPEN, deliberately: `MasterFormModal` starts its
        `/regions` fetch on mount, and this form already fetches the same
        list, so a permanently-mounted copy would double every region read.

        ⚠️ NESTED-FORM CONTAINMENT needs both of these — a portal is no
        containment at all, since React propagates by the REACT tree (the bug
        `PickerField` shipped once, `tsc`/oxlint green throughout):
          1. Mounted as a SIBLING of the form above, not inside it, so a
             submit from it doesn't bubble through `<form id={formId}>`.
          2. `MasterFormModal.handleSubmit` calls `stopPropagation()` anyway —
             the guard that survives (1) being moved.

        Escape/Tab need no work: `Modal` already stops both.
      */}
      {addingCity && (
        <MasterFormModal
          open
          kind="cities"
          record={null}
          onClose={() => setAddingCity(false)}
          onSaved={(saved) => {
            setAddingCity(false);
            // Neither "null on create" nor "wrong shape from kind='cities'"
            // is enforced by the prop's type, so a silent `as City` would
            // keep compiling if either ever changed — hence the narrowing.
            // `country`+`region` together are `City`'s real discriminant in
            // `MasterSavedRecord`.
            if (saved === null || !('country' in saved) || !('region' in saved)) return;
            setCreatedCities((previous) =>
              previous.some((city) => city.id === saved.id) ? previous : [...previous, saved],
            );
            // ⚠️ JOINS the draft; does not replace it. `cityIds` on a project
            // write is a whole-set REPLACE, so dropping existing entries here
            // would quietly clear every city already chosen.
            setForm((previous) =>
              previous.cityIds.includes(String(saved.id))
                ? previous
                : { ...previous, cityIds: [...previous.cityIds, String(saved.id)] },
            );
            clearError('cityIds');
          }}
        />
      )}
    </Modal>
  );
}

/**
 * A real `<fieldset>` / `<legend>` rather than a styled div, so each group is
 * announced along with its controls. Nothing collapses: a disclosure can hide a
 * server error inside a closed section.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="min-w-0 rounded-lg border border-border bg-surface">
      <legend className="mx-3 px-1 text-xs font-semibold text-fg">{title}</legend>
      <div className="min-w-0 px-3 pt-1 pb-3">{children}</div>
    </fieldset>
  );
}
